import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import { formatMoney } from '../utils/money';

const TEXT = {
  es: {
    title: 'Ganancia real por canal',
    subtitle: 'Ventas menos IVA (si aplica), costo de productos, env\u00edos, comisiones y publicidad. Solo pedidos pagados y enviados.',
    channel: 'Canal',
    orders: 'Pedidos',
    gross: 'Ventas',
    vat: 'IVA',
    vatNote: 'Las ventas incluyen IVA. La columna IVA ({{rate}} %) se resta de la ganancia porque tu r\u00e9gimen es el general; puedes cambiarlo en Configuraci\u00f3n.',
    productCost: 'Costo productos',
    shippingFees: 'Env\u00edos y comisiones',
    adSpend: 'Publicidad',
    realProfit: 'Ganancia real',
    general: 'General (sin canal concreto)',
    total: 'Total',
    noOrders: 'No hay pedidos pagados o enviados en este mes.',
    withoutItems: '{{count}} pedido(s) tienen art\u00edculos sin costo o ning\u00fan art\u00edculo, as\u00ed que su costo de producto est\u00e1 incompleto: la ganancia real es menor que la que se muestra.',
    otherCurrency: '{{count}} pedido(s) de este mes est\u00e1n en otra moneda y no se incluyen: no convertimos entre monedas.',
    zeroVat: '{{count}} pedido(s) de este mes llegaron con IVA 0. Si son ventas fuera de la UE puede ser correcto; si no, revisa los impuestos en tu tienda (Shopify, WooCommerce) o en el archivo importado, porque la ganancia real saldr\u00e1 inflada.',
    adHint: 'Escribe lo que gastaste en anuncios este mes en cada canal. Se guarda al salir del campo.',
    saved: 'Gasto guardado.',
    saveError: 'No se pudo guardar el gasto. Revisa que sea un n\u00famero v\u00e1lido.',
    loadError: 'No se pudo cargar el resumen.',
    loading: 'Cargando...',
    adReminder: 'Este mes no has registrado gasto en publicidad. Si has hecho anuncios, ap\u00fantalo en la columna Publicidad; si no, tu ganancia real saldr\u00e1 inflada.',
    adReminderDismiss: 'No hice publicidad este mes',
    prev: 'Mes anterior',
    next: 'Mes siguiente',
  },
  en: {
    title: 'Real profit by channel',
    subtitle: 'Sales minus VAT (if applicable), product cost, shipping, fees and advertising. Only paid and shipped orders.',
    channel: 'Channel',
    orders: 'Orders',
    gross: 'Sales',
    vat: 'VAT',
    vatNote: 'Sales include VAT. The VAT column ({{rate}}%) is deducted from profit because your regime is standard VAT; you can change it in Settings.',
    productCost: 'Product cost',
    shippingFees: 'Shipping & fees',
    adSpend: 'Advertising',
    realProfit: 'Real profit',
    general: 'General (no specific channel)',
    total: 'Total',
    noOrders: 'No paid or shipped orders this month.',
    withoutItems: '{{count}} order(s) have items without a cost or no items at all, so their product cost is incomplete: real profit is lower than shown.',
    otherCurrency: '{{count}} order(s) this month are in another currency and are not included: we do not convert between currencies.',
    zeroVat: '{{count}} order(s) this month arrived with 0 VAT. That can be right for sales outside the EU; otherwise check the tax settings in your store (Shopify, WooCommerce) or in the imported file, because your real profit will be overstated.',
    adHint: 'Enter what you spent on ads this month for each channel. It is saved when you leave the field.',
    saved: 'Ad spend saved.',
    saveError: 'The ad spend could not be saved. Check that it is a valid number.',
    loadError: 'The summary could not be loaded.',
    loading: 'Loading...',
    adReminder: 'No advertising spend has been logged for this month. If you ran ads, enter them in the Advertising column; otherwise your real profit will be overstated.',
    adReminderDismiss: 'I did not run ads this month',
    prev: 'Previous month',
    next: 'Next month',
  },
  fr: {
    title: 'B\u00e9n\u00e9fice r\u00e9el par canal',
    subtitle: 'Ventes moins TVA (le cas \u00e9ch\u00e9ant), co\u00fbt des produits, livraison, commissions et publicit\u00e9. Uniquement les commandes pay\u00e9es et exp\u00e9di\u00e9es.',
    channel: 'Canal',
    orders: 'Commandes',
    gross: 'Ventes',
    vat: 'TVA',
    vatNote: 'Les ventes incluent la TVA. La colonne TVA ({{rate}} %) est d\u00e9duite du b\u00e9n\u00e9fice car votre r\u00e9gime est le r\u00e9gime g\u00e9n\u00e9ral ; modifiable dans Param\u00e8tres.',
    productCost: 'Co\u00fbt produits',
    shippingFees: 'Livraison et commissions',
    adSpend: 'Publicit\u00e9',
    realProfit: 'B\u00e9n\u00e9fice r\u00e9el',
    general: 'G\u00e9n\u00e9ral (sans canal pr\u00e9cis)',
    total: 'Total',
    noOrders: 'Aucune commande pay\u00e9e ou exp\u00e9di\u00e9e ce mois-ci.',
    withoutItems: '{{count}} commande(s) ont des articles sans co\u00fbt ou aucun article : leur co\u00fbt produit est incomplet et le b\u00e9n\u00e9fice r\u00e9el est inf\u00e9rieur \u00e0 celui affich\u00e9.',
    otherCurrency: '{{count}} commande(s) de ce mois sont dans une autre devise et ne sont pas incluses : nous ne convertissons pas entre devises.',
    zeroVat: '{{count}} commande(s) de ce mois sont arriv\u00e9es avec une TVA de 0. C\u2019est normal pour des ventes hors UE ; sinon, v\u00e9rifiez les taxes de votre boutique (Shopify, WooCommerce) ou du fichier import\u00e9, car votre b\u00e9n\u00e9fice r\u00e9el sera surestim\u00e9.',
    adHint: 'Indiquez vos d\u00e9penses publicitaires du mois pour chaque canal. Elles sont enregistr\u00e9es en quittant le champ.',
    saved: 'D\u00e9pense enregistr\u00e9e.',
    saveError: 'Impossible d\u2019enregistrer la d\u00e9pense. V\u00e9rifiez que le nombre est valide.',
    loadError: 'Impossible de charger le r\u00e9sum\u00e9.',
    loading: 'Chargement...',
    adReminder: 'Aucune d\u00e9pense publicitaire n\u2019est enregistr\u00e9e pour ce mois. Si vous avez fait de la publicit\u00e9, saisissez-la dans la colonne Publicit\u00e9 ; sinon, votre b\u00e9n\u00e9fice r\u00e9el sera surestim\u00e9.',
    adReminderDismiss: 'Pas de publicit\u00e9 ce mois-ci',
    prev: 'Mois pr\u00e9c\u00e9dent',
    next: 'Mois suivant',
  },
};

const getText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function ProfitSummary({ token, onAuthError }) {
  const { i18n } = useTranslation();
  const tx = getText(i18n.language);
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adInputs, setAdInputs] = useState({});
  const [status, setStatus] = useState('');
  const [noAdsMonths, setNoAdsMonths] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('omniflow_noads_months') || '[]');
    } catch (err) {
      return [];
    }
  });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const money = (value) =>
    formatMoney(value, i18n.language, data && data.currency);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/profit-summary?month=${month}`, authHeaders);
      const result = response.data;
      setData(result);
      const inputs = { general: result.general_ad_spend ? String(result.general_ad_spend) : '' };
      result.channels.forEach((c) => {
        inputs[c.channel] = c.ad_spend ? String(c.ad_spend) : '';
      });
      setAdInputs(inputs);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(tx.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setStatus(''); load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const saveAd = async (key, label) => {
    if (!data) return;
    const raw = (adInputs[key] || '').trim().replace(',', '.');
    const amount = raw === '' ? 0 : Number(raw);
    if (!Number.isFinite(amount) || amount < 0) {
      setStatus(tx.saveError);
      return;
    }
    const current = key === 'general'
      ? data.general_ad_spend
      : ((data.channels.find((c) => c.channel === key) || {}).ad_spend || 0);
    if (Math.abs(amount - (current || 0)) < 0.005) return;
    try {
      await axios.put(`${API_URL}/api/v1/ad-spend`, { month, channel: label, amount }, authHeaders);
      setStatus(tx.saved);
      load();
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setStatus(tx.saveError);
    }
  };

  const [year, mon] = month.split('-').map(Number);
  const monthLabel = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(new Date(year, mon - 1, 1));
  const monthTitle = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
  const isCurrentMonth = month >= currentMonth();
  const showVat = !!data && data.vat_regime === 'general';
  const showAdReminder = !!data && !noAdsMonths.includes(month)
    && data.channels.some((c) => c.orders > 0) && Number(data.totals.ad_spend || 0) === 0
    && !data.channels.some((c) => c.ad_source === 'meta') && data.general_ad_source !== 'meta';
  const dismissAdReminder = () => {
    const next = [...noAdsMonths, month];
    setNoAdsMonths(next);
    try {
      localStorage.setItem('omniflow_noads_months', JSON.stringify(next));
    } catch (err) {
      // Sin almacenamiento disponible: el aviso volvera a salir al recargar.
    }
  };

  const th = 'text-muted text-xs text-left pb-3 pr-4 uppercase';
  const td = 'py-3 pr-4 text-sm';
  const adInput = (key, label) => (
    <input
      type="text"
      inputMode="decimal"
      placeholder="0"
      value={adInputs[key] ?? ''}
      onChange={(e) => setAdInputs((v) => ({ ...v, [key]: e.target.value }))}
      onBlur={() => saveAd(key, label)}
      className="bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-sm w-24 focus:outline-none focus:border-accent"
    />
  );

  return (
    <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mb-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div>
          <h3 className="text-white text-base font-semibold">{tx.title}</h3>
          <p className="text-muted text-xs mt-1">{tx.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            aria-label={tx.prev}
            className="bg-surfaceAlt border border-edge text-white text-sm px-3 py-1.5 rounded-lg cursor-pointer"
          >
            {'\u2039'}
          </button>
          <span className="text-white text-sm min-w-[140px] text-center">{monthTitle}</span>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            disabled={isCurrentMonth}
            aria-label={tx.next}
            className={`bg-surfaceAlt border border-edge text-white text-sm px-3 py-1.5 rounded-lg ${isCurrentMonth ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {'\u203A'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">{error}</div>
      )}

      {loading && !data ? (
        <p className="text-muted text-center py-8">{tx.loading}</p>
      ) : data && (
        <>
          {showAdReminder && (
            <div className="bg-[#0f1d2a] border border-[#1d4f7f] rounded-lg p-3 mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[#7cc4ff] text-sm">{tx.adReminder}</p>
              <button
                type="button"
                onClick={dismissAdReminder}
                className="text-muted text-xs px-3 py-1.5 rounded border border-edge cursor-pointer shrink-0"
              >
                {tx.adReminderDismiss}
              </button>
            </div>
          )}
          {data.other_currency_orders > 0 && (
            <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-3 mb-4">
              <p className="text-amber-400 text-sm">{tx.otherCurrency.replace('{{count}}', data.other_currency_orders)}</p>
            </div>
          )}
          {data.vat_regime === 'general' && data.zero_vat_orders > 0 && (
            <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-3 mb-4">
              <p className="text-amber-400 text-sm">{tx.zeroVat.replace('{{count}}', data.zero_vat_orders)}</p>
            </div>
          )}
          {data.totals.orders_without_items > 0 && (
            <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-3 mb-4">
              <p className="text-amber-400 text-sm">{tx.withoutItems.replace('{{count}}', data.totals.orders_without_items)}</p>
            </div>
          )}
          {showVat && <p className="text-muted text-xs mb-2">{tx.vatNote.replace('{{rate}}', String(data.vat_rate))}</p>}
          <p className="text-muted text-xs mb-3">{tx.adHint}</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="border-b border-edge">
                  <th className={th}>{tx.channel}</th>
                  <th className={th}>{tx.orders}</th>
                  <th className={th}>{tx.gross}</th>
                  {showVat && <th className={th}>{tx.vat}</th>}
                  <th className={th}>{tx.productCost}</th>
                  <th className={th}>{tx.shippingFees}</th>
                  <th className={th}>{tx.adSpend}</th>
                  <th className={th}>{tx.realProfit}</th>
                </tr>
              </thead>
              <tbody>
                {data.channels.map((c) => (
                  <tr key={c.channel} className="border-b border-surfaceAlt">
                    <td className={`${td} text-white`}>{c.label}</td>
                    <td className={`${td} text-muted`}>{c.orders}</td>
                    <td className={`${td} text-white`}>{money(c.gross)}</td>
                    {showVat && <td className={`${td} text-muted`}>{money(c.vat)}</td>}
                    <td className={`${td} text-muted`}>{money(c.product_cost)}</td>
                    <td className={`${td} text-muted`}>{money(c.shipping + c.fees)}</td>
                    <td className={td}>{c.ad_source === 'meta' ? <span className="text-[#7cc4ff] text-sm whitespace-nowrap">{money(c.ad_spend)}{' \u00b7 Meta'}</span> : adInput(c.channel, c.label)}</td>
                    <td className={`${td} font-semibold ${c.real_profit >= 0 ? 'text-accent' : 'text-red-400'}`}>{money(c.real_profit)}</td>
                  </tr>
                ))}
                <tr className="border-b border-surfaceAlt">
                  <td className={`${td} text-muted`}>{tx.general}</td>
                  <td className={`${td} text-muted`}>{'\u2014'}</td>
                  <td className={`${td} text-muted`}>{'\u2014'}</td>
                  <td className={`${td} text-muted`}>{'\u2014'}</td>
                  <td className={`${td} text-muted`}>{'\u2014'}</td>
                  {showVat && <td className={`${td} text-muted`}>{'\u2014'}</td>}
                  <td className={td}>{data.general_ad_source === 'meta' ? <span className="text-[#7cc4ff] text-sm whitespace-nowrap">{money(data.general_ad_spend)}{' \u00b7 Meta'}</span> : adInput('general', 'general')}</td>
                  <td className={`${td} text-muted`}>{'\u2014'}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td className={`${td} text-white font-bold`}>{tx.total}</td>
                  <td className={`${td} text-white font-bold`}>{data.totals.orders}</td>
                  <td className={`${td} text-white font-bold`}>{money(data.totals.gross)}</td>
                  {showVat && <td className={`${td} text-white font-bold`}>{money(data.totals.vat)}</td>}
                  <td className={`${td} text-white font-bold`}>{money(data.totals.product_cost)}</td>
                  <td className={`${td} text-white font-bold`}>{money(data.totals.shipping + data.totals.fees)}</td>
                  <td className={`${td} text-white font-bold`}>{money(data.totals.ad_spend)}</td>
                  <td className={`${td} font-bold ${data.totals.real_profit >= 0 ? 'text-accent' : 'text-red-400'}`}>{money(data.totals.real_profit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {data.channels.length === 0 && <p className="text-muted text-sm text-center py-4">{tx.noOrders}</p>}
          {status && <p className="text-muted text-xs mt-3">{status}</p>}
        </>
      )}
    </div>
  );
}

export default ProfitSummary;